import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Calendar, PieChart, BarChart3, Filter, Search, ArrowUpRight, ArrowDownRight, Target, Zap, Clock, Brain } from 'lucide-react';
import { PieChart as RePieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { projectBudgetService, type ProjectBudgetSummary } from '../../services/projectBudget';
import { toast } from 'sonner';

interface BudgetManagementProps {
  projectId?: number;
  projectCode?: string;
}

// Demo data — used ONLY when projectId is unset (preview / marketing mode).
// Real projects must never silently fall through to this seed; an outage or
// fresh project should render an empty state, not fake numbers.
export function BudgetManagement({ projectId }: BudgetManagementProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [xAxisView, setXAxisView] = useState<'months' | 'gates'>('months');
  const [summary, setSummary] = useState<ProjectBudgetSummary | null>(null);
  const [loading, setLoading] = useState(!!projectId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    projectBudgetService
      .getSummary(projectId)
      .then(setSummary)
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Failed to load budget');
        toast.error('Failed to load budget summary');
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  // Use API data when available, else fallback for non-workspace or loading
  const totalBudget = summary?.total_budget ?? 0;
  const spentBudget = summary?.spent_budget ?? 0;
  const committedBudget = summary?.committed_budget ?? 0;
  const remainingBudget = summary?.remaining_budget ?? totalBudget - spentBudget - committedBudget;
  const utilizationRate = summary?.utilization_rate ?? (totalBudget > 0 ? ((spentBudget + committedBudget) / totalBudget) * 100 : 0);

  const projectStartDate = summary?.project_start_date ? new Date(summary.project_start_date) : new Date();
  const projectEndDate = summary?.project_end_date ? new Date(summary.project_end_date) : new Date();
  const currentDate = new Date();
  // Fiscal year end = Dec 31 of the current calendar year. Don't hardcode a
  // year — that ages out and lies to users in following years.
  const fiscalYear = currentDate.getFullYear();
  const yearEndDate = new Date(fiscalYear, 11, 31);
  const totalDays = (projectEndDate.getTime() - projectStartDate.getTime()) / (1000 * 60 * 60 * 24) || 1;
  const daysElapsed = (currentDate.getTime() - projectStartDate.getTime()) / (1000 * 60 * 60 * 24);
  const timeProgress = summary?.time_progress ?? (totalDays > 0 ? (daysElapsed / totalDays) * 100 : 0);

  // Real projects: surface whatever the API returned (even empty). Demo data
  // is reserved for the preview-mode (!projectId) branch.
  const apiCategories = summary?.budget_by_category ?? [];
  const sourceCategories = apiCategories;
  const budgetByCategory = sourceCategories.map((c) => ({
    category: c.category,
    allocated: c.allocated,
    spent: c.spent,
    committed: c.committed,
    color: c.color || '#3B82F6',
  }));
  const hasCategoryData = budgetByCategory.length > 0;

  // Chart series — same rule: empty arrays for real projects with no data.
  // The synthesised single-row fallback was misleading (it drew a flat line
  // labelled "—" that looked like real spend).
  const monthlyData = summary?.monthly_data ?? (projectId ? [] : [
    { month: 'Jan', planned: 40000, actual: 38000, forecast: 38000 },
    { month: 'Feb', planned: 80000, actual: 76000, forecast: 76000 },
    { month: 'Mar', planned: 120000, actual: 118000, forecast: 118000 },
  ]);
  const gatesData = summary?.gates_data ?? (projectId ? [] : [
    { gate: 'Initiation', planned: 30000, actual: 28000, forecast: 28000 },
    { gate: 'Planning', planned: 70000, actual: 68000, forecast: 68000 },
    { gate: 'Execution', planned: 150000, actual: 141000, forecast: 152000 },
  ]);

  const chartData = xAxisView === 'months' ? monthlyData : gatesData;
  const xAxisKey = xAxisView === 'months' ? 'month' : 'gate';

  const projectedEndOfProject = summary?.projected_end_of_project ?? spentBudget;
  const varianceEndOfProject = summary?.variance_end_of_project ?? projectedEndOfProject - totalBudget;
  const projectedEndOfYear = summary?.projected_end_of_year ?? projectedEndOfProject;
  const varianceEndOfYear = summary?.variance_end_of_year ?? projectedEndOfYear - totalBudget;

  const recentTransactions = summary?.recent_transactions ?? [];
  const milestones = summary?.milestones ?? [{ phase: 'Project', budget: totalBudget, spent: spentBudget, status: 'In Progress', progress: summary?.progress ?? 0 }];

  const currentDistribution = budgetByCategory.map(cat => ({
    name: cat.category,
    value: cat.spent + cat.committed,
    color: cat.color
  }));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (percentage: number) => {
    if (percentage <= 75) return 'text-green-600 bg-green-50 border-green-200';
    if (percentage <= 90) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  if (projectId && loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto mb-4" />
          <p className="text-[#486581]">Loading budget...</p>
        </div>
      </div>
    );
  }

  if (projectId && error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-2" />
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#486581] via-[#5c748e] to-[#486581] rounded-xl p-8 text-white border-b-4 border-[#DAA520]">
        <div className="mb-6">
          <h1 className="text-3xl mb-2">Budget Management</h1>
          <p className="text-[#d4dce5]">Track, analyze, and forecast project financial performance</p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Total Budget</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{formatCurrency(totalBudget)}</div>
            <div className="text-sm text-gray-600">
              Allocated funds
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Spent to Date</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{formatCurrency(spentBudget)}</div>
            <div className="text-sm text-green-600">
              {((spentBudget / totalBudget) * 100).toFixed(1)}% utilized
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Committed</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{formatCurrency(committedBudget)}</div>
            <div className="text-sm text-orange-600">
              Pending transactions
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Remaining</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{formatCurrency(remainingBudget)}</div>
            <div className={`text-sm ${remainingBudget > 0 ? 'text-purple-600' : 'text-red-600'}`}>
              {((remainingBudget / totalBudget) * 100).toFixed(1)}% available
            </div>
          </div>
        </div>
      </div>

      {/* Budget Consumption Analysis - TOP SECTION */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main Graph - 2 columns */}
        <div className="col-span-2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl mb-1">Budget Consumption Analysis</h2>
              <p className="text-sm text-gray-600">Planned vs Actual spending trajectory</p>
            </div>
            <BarChart3 className="w-6 h-6 text-gray-400" />
          </div>

          {/* X-Axis Selector */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-gray-600">View by:</span>
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setXAxisView('months')}
                className={`px-4 py-2 rounded-md text-sm transition-colors ${
                  xAxisView === 'months'
                    ? 'bg-white text-blue-600 shadow-sm font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Months
              </button>
              <button
                onClick={() => setXAxisView('gates')}
                className={`px-4 py-2 rounded-md text-sm transition-colors ${
                  xAxisView === 'gates'
                    ? 'bg-white text-blue-600 shadow-sm font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Project Gates
              </button>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorPlanned" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey={xAxisKey} stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} tickFormatter={(value) => `$${value/1000}k`} />
              <Tooltip 
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Area type="monotone" dataKey="planned" stroke="#3B82F6" fillOpacity={1} fill="url(#colorPlanned)" name="Planned Budget" strokeWidth={2} />
              <Area type="monotone" dataKey="actual" stroke="#10B981" fillOpacity={1} fill="url(#colorActual)" name="Actual Spend" strokeWidth={3} />
              <Area type="monotone" dataKey="forecast" stroke="#F59E0B" fillOpacity={1} fill="url(#colorForecast)" name="Forecast" strokeDasharray="5 5" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Budget Analysis — derived from the real summary numbers, not
            hardcoded paragraphs. When there's no data, show an empty state
            instead of inventing numbers. */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-medium text-blue-900">Budget Analysis</h3>
          </div>

          {totalBudget <= 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>Set a budget on this project to see analysis.</p>
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Current Status</h4>
                <p className="text-gray-700 leading-relaxed">
                  Utilization is{' '}
                  <span className={`font-medium ${
                    utilizationRate > timeProgress + 10 ? 'text-red-700' :
                    utilizationRate > timeProgress ? 'text-yellow-700' :
                    'text-green-700'
                  }`}>
                    {utilizationRate.toFixed(1)}%
                  </span>{' '}
                  against timeline progress of{' '}
                  <span className="font-medium text-gray-900">{timeProgress.toFixed(1)}%</span>.
                </p>
              </div>

              <div className="border-t border-blue-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Projection</h4>
                <p className="text-gray-700 leading-relaxed">
                  Projected end-of-project spend is{' '}
                  <span className="font-medium text-gray-900">{formatCurrency(projectedEndOfProject)}</span>,{' '}
                  <span className={`font-medium ${varianceEndOfProject > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {varianceEndOfProject > 0 ? 'over' : 'under'} budget by {formatCurrency(Math.abs(varianceEndOfProject))}
                  </span>.
                </p>
              </div>

              <div className="border-t border-blue-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Suggested next steps</h4>
                <ul className="space-y-2 text-gray-700">
                  {utilizationRate > timeProgress + 10 && (
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Burn rate is outpacing schedule — review high-spend categories.</span>
                    </li>
                  )}
                  {committedBudget > 0 && (
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{formatCurrency(committedBudget)} is committed; confirm pending POs.</span>
                    </li>
                  )}
                  {remainingBudget < 0 && (
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Project is over budget — request additional funding or rescope.</span>
                    </li>
                  )}
                  {utilizationRate <= timeProgress && remainingBudget >= 0 && (
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Spend is tracking with timeline — no action required.</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Total Budget</span>
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <div className="text-2xl mb-1">{formatCurrency(totalBudget)}</div>
          <div className="text-xs text-gray-500">Project allocation</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Spent</span>
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingDown className="w-4 h-4 text-red-600" />
            </div>
          </div>
          <div className="text-2xl mb-1">{formatCurrency(spentBudget)}</div>
          <div className="text-xs text-gray-500">{((spentBudget / totalBudget) * 100).toFixed(1)}% utilized</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Committed</span>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-4 h-4 text-yellow-600" />
            </div>
          </div>
          <div className="text-2xl mb-1">{formatCurrency(committedBudget)}</div>
          <div className="text-xs text-gray-500">{((committedBudget / totalBudget) * 100).toFixed(1)}% pending</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Remaining</span>
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="w-4 h-4 text-green-600" />
            </div>
          </div>
          <div className="text-2xl mb-1">{formatCurrency(remainingBudget)}</div>
          <div className="text-xs text-gray-500">{((remainingBudget / totalBudget) * 100).toFixed(1)}% available</div>
        </div>

        <div className={`p-6 rounded-lg border ${getStatusColor(utilizationRate)}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm">Utilization Rate</span>
            <div className="p-2 bg-white/50 rounded-lg">
              <Target className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl mb-1">{utilizationRate.toFixed(1)}%</div>
          <div className="text-xs">
            {utilizationRate <= 75 ? 'On track' : utilizationRate <= 90 ? 'Monitor closely' : 'Over budget risk'}
          </div>
        </div>
      </div>

      {/* Budget vs Time Progress Alert */}
      <div className={`p-4 rounded-lg border flex items-start gap-3 ${
        utilizationRate > timeProgress 
          ? 'bg-yellow-50 border-yellow-200' 
          : 'bg-green-50 border-green-200'
      }`}>
        <AlertTriangle className={`w-5 h-5 mt-0.5 ${
          utilizationRate > timeProgress ? 'text-yellow-600' : 'text-green-600'
        }`} />
        <div className="flex-1">
          <h3 className={`font-medium mb-1 ${
            utilizationRate > timeProgress ? 'text-yellow-900' : 'text-green-900'
          }`}>
            {utilizationRate > timeProgress 
              ? 'Budget Burn Rate Above Timeline Progress' 
              : 'Budget Utilization Aligned with Timeline'}
          </h3>
          <p className={`text-sm ${
            utilizationRate > timeProgress ? 'text-yellow-700' : 'text-green-700'
          }`}>
            Project is {timeProgress.toFixed(1)}% complete by timeline, but {utilizationRate.toFixed(1)}% of budget is utilized.
            {utilizationRate > timeProgress 
              ? ' Consider reviewing spending patterns to align with project schedule.' 
              : ' Budget consumption is healthy and proportional to project progress.'}
          </p>
        </div>
      </div>

      {/* Projections Cards */}
      <div className="grid grid-cols-2 gap-6">
        {/* End of Project Projection */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg mb-1">End of Project Projection</h3>
              <p className="text-sm text-gray-600">
                Expected completion: {projectEndDate && !isNaN(projectEndDate.getTime()) ? projectEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
              </p>
            </div>
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-600 mb-1">Projected Spend</div>
              <div className="text-2xl text-blue-900">{formatCurrency(projectedEndOfProject)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Variance</div>
              <div className={`text-2xl flex items-center gap-1 ${
                varianceEndOfProject < 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {varianceEndOfProject < 0 ? <ArrowDownRight className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
                {formatCurrency(Math.abs(varianceEndOfProject))}
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Progress to completion</span>
              <span className="font-medium">{((projectedEndOfProject / totalBudget) * 100).toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${(projectedEndOfProject / totalBudget) * 100}%` }}
              />
            </div>
            <div className="mt-3 text-xs text-gray-600">
              Based on current burn rate and committed expenses
            </div>
          </div>
        </div>

        {/* End of Year Projection */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg mb-1">End of Year Projection</h3>
              <p className="text-sm text-gray-600">
                Fiscal year end: {yearEndDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-600 mb-1">Projected Spend</div>
              <div className="text-2xl text-purple-900">{formatCurrency(projectedEndOfYear)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Over Budget</div>
              <div className={`text-2xl flex items-center gap-1 ${
                varianceEndOfYear < 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {varianceEndOfYear < 0 ? <ArrowDownRight className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
                {formatCurrency(Math.abs(varianceEndOfYear))}
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Budget utilization by year end</span>
              <span className="font-medium">{((projectedEndOfYear / totalBudget) * 100).toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all"
                style={{ width: `${Math.min((projectedEndOfYear / totalBudget) * 100, 100)}%` }}
              />
            </div>
            <div className="mt-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-gray-600">
                Projected to exceed budget due to post-project maintenance and support costs
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6">
          {['overview', 'categories', 'timeline', 'transactions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-2 gap-6">
          {/* Burn Rate Chart */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg">Budget Burn Rate & Forecast</h3>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorPlanned" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey={xAxisKey} stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} tickFormatter={(value) => `$${value/1000}k`} />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                />
                <Legend />
                <Area type="monotone" dataKey="planned" stroke="#3B82F6" fillOpacity={1} fill="url(#colorPlanned)" name="Planned" />
                <Area type="monotone" dataKey="actual" stroke="#10B981" fillOpacity={1} fill="url(#colorActual)" name="Actual" strokeWidth={2} />
                <Area type="monotone" dataKey="forecast" stroke="#F59E0B" fillOpacity={1} fill="url(#colorForecast)" name="Forecast" strokeDasharray="5 5" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Budget Distribution */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg">Current Budget Distribution</h3>
              <PieChart className="w-5 h-5 text-gray-400" />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <RePieChart>
                <Pie
                  data={currentDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {currentDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && !hasCategoryData && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
          <PieChart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm mb-2">No budget categories yet.</p>
          <p className="text-xs">Add categories to break down spend by area.</p>
        </div>
      )}
      {activeTab === 'categories' && hasCategoryData && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Category</th>
                  <th className="px-6 py-3 text-right text-xs uppercase tracking-wider text-gray-600">Allocated</th>
                  <th className="px-6 py-3 text-right text-xs uppercase tracking-wider text-gray-600">Spent</th>
                  <th className="px-6 py-3 text-right text-xs uppercase tracking-wider text-gray-600">Committed</th>
                  <th className="px-6 py-3 text-right text-xs uppercase tracking-wider text-gray-600">Remaining</th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-gray-600">Utilization</th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {budgetByCategory.map((category) => {
                  const remaining = category.allocated - category.spent - category.committed;
                  const utilization = ((category.spent + category.committed) / category.allocated) * 100;
                  
                  return (
                    <tr key={category.category} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }} />
                          <span className="font-medium">{category.category}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">{formatCurrency(category.allocated)}</td>
                      <td className="px-6 py-4 text-right text-red-600">{formatCurrency(category.spent)}</td>
                      <td className="px-6 py-4 text-right text-yellow-600">{formatCurrency(category.committed)}</td>
                      <td className="px-6 py-4 text-right text-green-600">{formatCurrency(remaining)}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-sm font-medium">{utilization.toFixed(1)}%</span>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${
                                utilization <= 75 ? 'bg-green-500' : utilization <= 90 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(utilization, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {utilization <= 75 ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">On Track</span>
                        ) : utilization <= 90 ? (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Monitor</span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">At Risk</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-6 py-4 font-medium">Total</td>
                  <td className="px-6 py-4 text-right font-medium">{formatCurrency(totalBudget)}</td>
                  <td className="px-6 py-4 text-right font-medium text-red-600">{formatCurrency(spentBudget)}</td>
                  <td className="px-6 py-4 text-right font-medium text-yellow-600">{formatCurrency(committedBudget)}</td>
                  <td className="px-6 py-4 text-right font-medium text-green-600">{formatCurrency(remainingBudget)}</td>
                  <td className="px-6 py-4 text-center font-medium">{utilizationRate.toFixed(1)}%</td>
                  <td className="px-6 py-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="space-y-6">
          {/* Phase-wise Budget */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg mb-6">Budget by Project Phase</h3>
            <div className="space-y-4">
              {milestones.map((milestone) => (
                <div key={milestone.phase} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        milestone.status === 'Completed' ? 'bg-green-100' :
                        milestone.status === 'In Progress' ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        {milestone.status === 'Completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : milestone.status === 'In Progress' ? (
                          <Zap className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{milestone.phase}</div>
                        <div className="text-sm text-gray-600">{milestone.status}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatCurrency(milestone.spent)} / {formatCurrency(milestone.budget)}</div>
                      <div className="text-sm text-gray-600">{milestone.progress}% complete</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          milestone.status === 'Completed' ? 'bg-green-500' :
                          milestone.status === 'In Progress' ? 'bg-blue-500' : 'bg-gray-400'
                        }`}
                        style={{ width: `${milestone.progress}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-600 w-12">{milestone.progress}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-80"
                />
              </div>
              <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Showing {recentTransactions.length} transactions</span>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Date</th>
                  <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Category</th>
                  <th className="px-6 py-3 text-left text-xs uppercase tracking-wider text-gray-600">Description</th>
                  <th className="px-6 py-3 text-right text-xs uppercase tracking-wider text-gray-600">Amount</th>
                  <th className="px-6 py-3 text-center text-xs uppercase tracking-wider text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(transaction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        {transaction.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">{transaction.description}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`font-medium ${transaction.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(Math.abs(transaction.amount))}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        transaction.status === 'Completed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {transaction.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}